import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { todayYmd } from "@/utils/dateUtils";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  CalendarClock, 
  Plus, 
  Trash2, 
  Users, 
  Calendar as CalendarIcon,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  XCircle,
  Loader2
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";

import { 
  API, 
  priorityColors, 
  dayTypeLabels, 
  roleLabels 
} from './mesaiConstants';
import { getRoleTitle } from './mesaiHelpers';
import DraggableModal from './DraggableModal';
import { useRoleFlags } from '@/auth/useRoleFlags';

const MesaiAnketiSection = ({ userRole, personnel, allPersonnel: allPersonnelProp = [], parks, onCreatePlanFromSurvey, personnelId }) => {
  // Sprint 2 F3: Dinamik yetki + rol flag'leri
  const flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = flagsCtx;

  const normalizeRoleKey = useCallback((role) => {
    const raw = String(role || '').trim().toLowerCase();
    const ascii = raw
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ı/g, 'i')
      .replace(/İ/g, 'i')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c');
    return ascii.replace(/\s+/g, '_');
  }, []);

  const normalizedUserRole = normalizeRoleKey(userRole);
  const isRole = useCallback((person, expectedRole) => normalizeRoleKey(person?.role) === expectedRole, [normalizeRoleKey]);
  const isBolgeMuhendisiLike = useCallback((person) => {
    const roleKey = normalizeRoleKey(person?.role);
    return roleKey === 'bolge_muhendisi' || roleKey.includes('muhendis');
  }, [normalizeRoleKey]);
  const isFormenLike = useCallback((person) => {
    const roleKey = normalizeRoleKey(person?.role);
    return roleKey === 'formen';
  }, [normalizeRoleKey]);
  const isUnderFormen = useCallback((person, formenId) => {
    if (!person || !formenId) return false;
    return person.formen_id === formenId || person.parent_id === formenId;
  }, []);

  const [fallbackPersonnel, setFallbackPersonnel] = useState([]);
  const [dialogPersonnel, setDialogPersonnel] = useState([]);
  const [dialogBmOptions, setDialogBmOptions] = useState([]);
  const [dialogFormenOptions, setDialogFormenOptions] = useState([]);
  const [directBmOptions, setDirectBmOptions] = useState([]);
  const activePersonnel = (personnel && personnel.length > 0) ? personnel : fallbackPersonnel;
  const optionPersonnel = dialogPersonnel.length > 0 ? dialogPersonnel : activePersonnel;
  // Görünürlük ve hiyerarşi kararlarında pasif/eski kayıtlar da gerekebildiği için
  // mümkünse tam personel listesini kullan.
  const visibilityPersonnel = (allPersonnelProp && allPersonnelProp.length > 0)
    ? allPersonnelProp
    : activePersonnel;
  const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
  // Çoklu panel kullanımında oturum cache sapmalarına karşı önce panelin gerçek personnelId bilgisini kullan.
  const effectivePersonnelId = personnelId || auth.personnelId;

  // Hiyerarşik hedef seçimi (Mobil Ekip mantığı)
  const [selectedSurveyBolgeMuh, setSelectedSurveyBolgeMuh] = useState(null);
  const [selectedSurveyFormen, setSelectedSurveyFormen] = useState(null);

  const isVisibleForMy = useCallback((bm) => {
    if (!bm || !effectivePersonnelId) return false;
    if (bm.mudur_yardimcisi_id === effectivePersonnelId) return true;
    if (bm.parent_id === effectivePersonnelId) return true;
    // Veri tutarsızlıklarında, BM altındaki formenlerden MY eşleşmesini de kabul et
    const anyFormenBoundToMy = optionPersonnel.some(f =>
      isRole(f, 'formen') &&
      f.bolge_muhendisi_id === bm.id &&
      (f.mudur_yardimcisi_id === effectivePersonnelId || f.parent_id === effectivePersonnelId)
    );
    return anyFormenBoundToMy;
  }, [optionPersonnel, effectivePersonnelId, isRole]);
  const bmSource = directBmOptions.length > 0
    ? directBmOptions
    : (dialogBmOptions.length > 0 ? dialogBmOptions : optionPersonnel.filter(p => isBolgeMuhendisiLike(p)));
  const formenSource = dialogFormenOptions.length > 0 ? dialogFormenOptions : optionPersonnel.filter(p => isFormenLike(p));
  const bolgeMuhendisleri = bmSource
    .filter(p => isBolgeMuhendisiLike(p))
    .filter(p => {
      if (normalizedUserRole === 'mudur') return true;
      if (normalizedUserRole === 'mudur_yardimcisi') return isVisibleForMy(p);
      return false;
    })
    .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'tr'));
  const bolgeMuhendisleriFallback = bmSource
    .filter(p => isBolgeMuhendisiLike(p))
    .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'tr'));
  const visibleBolgeMuhendisleri = bolgeMuhendisleri.length > 0 ? bolgeMuhendisleri : bolgeMuhendisleriFallback;
  const formenlerForSelectedBM = formenSource
    .filter(p => isFormenLike(p))
    .filter(p => p.bolge_muhendisi_id === selectedSurveyBolgeMuh)
    .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'tr'));
  const formenlerForCurrentBM = formenSource
    .filter(p => isFormenLike(p))
    .filter(p => p.bolge_muhendisi_id === effectivePersonnelId)
    .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'tr'));

  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false); // Değişiklik onayı
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveyResponses, setSurveyResponses] = useState([]);
  const [myCurrentResponse, setMyCurrentResponse] = useState(null); // Personelin mevcut cevabı
  const [existingPlans, setExistingPlans] = useState([]); // Bu anket için oluşturulmuş planlar
  
  // Silme onay diyalogu
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState(null);
  
  // Anketi kapatma onay diyalogu
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [surveyToClose, setSurveyToClose] = useState(null);
  
  // Create Survey Form
  const [surveyTitle, setSurveyTitle] = useState('');
  const [surveyDescription, setSurveyDescription] = useState('');
  const [targetDates, setTargetDates] = useState([]);
  const [customDate, setCustomDate] = useState(''); // Manuel tarih girişi
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineTime, setDeadlineTime] = useState('18:00');
  
  // Oluşturan kişinin katılım durumu
  const [creatorJoining, setCreatorJoining] = useState(null); // null: henüz belirlenmedi, true: katılacak, false: katılmayacak
  const [creatorNotJoiningReason, setCreatorNotJoiningReason] = useState('');
  
  // Gelemiyorum diyalogu için
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [surveyToDecline, setSurveyToDecline] = useState(null);
  // Kullanıcının cevapladığı anketleri kontrol et
  const [myResponses, setMyResponses] = useState({});
  const myResponsesRef = useRef({});
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);
  
  // Response Form
  const [selectedDates, setSelectedDates] = useState([]);

  const canCreateSurvey = ['formen', 'bolge_muhendisi', 'mudur_yardimcisi', 'mudur'].includes(normalizedUserRole);
  const canBypassDeadline = ['formen', 'bolge_muhendisi', 'mudur_yardimcisi', 'mudur'].includes(normalizedUserRole);
  const canDeleteSurvey = ['mudur', 'mudur_yardimcisi'].includes(normalizedUserRole);
  
  // Müdür ve Müdür Yardımcısı cevap veremez (amir)
  const canRespond = !['mudur', 'mudur_yardimcisi'].includes(normalizedUserRole);

  useEffect(() => {
    if (personnel && personnel.length > 0) return;
    let mounted = true;
    const loadFallbackPersonnel = async () => {
      try {
        const response = await axios.get(`${API}/api/personnel`);
        if (!mounted) return;
        const data = Array.isArray(response.data) ? response.data : [];
        setFallbackPersonnel(data.filter(p => (p?.status || 'active') === 'active'));
      } catch (error) {
        console.error('Fallback personnel fetch error:', error);
      }
    };
    loadFallbackPersonnel();
    return () => { mounted = false; };
  }, [personnel]);

  useEffect(() => {
    let mounted = true;
    const loadDialogPersonnel = async () => {
      try {
        const allRes = await axios.get(`${API}/api/personnel`);
        if (mounted) {
          const data = Array.isArray(allRes.data) ? allRes.data : [];
          setDialogPersonnel(data.filter(p => (p?.status || 'active') === 'active'));
        }
      } catch (error) {
        console.error('Dialog all-personnel fetch error:', error);
      }

      try {
        const bmRes = await axios.get(`${API}/api/personnel`, { params: { role: 'bolge_muhendisi' } });
        if (mounted) {
          const bmData = Array.isArray(bmRes.data) ? bmRes.data : [];
          setDialogBmOptions(bmData.filter(p => (p?.status || 'active') === 'active'));
        }
      } catch (error) {
        console.error('Dialog BM fetch error:', error);
      }

      try {
        const formenRes = await axios.get(`${API}/api/personnel`, { params: { role: 'formen' } });
        if (mounted) {
          const formenData = Array.isArray(formenRes.data) ? formenRes.data : [];
          setDialogFormenOptions(formenData.filter(p => (p?.status || 'active') === 'active'));
        }
      } catch (error) {
        console.error('Dialog formen fetch error:', error);
      }
    };

    // Mount'ta yükle, dialog açıldığında da tazele
    if (showCreateDialog || dialogBmOptions.length === 0 || dialogFormenOptions.length === 0) {
      loadDialogPersonnel();
    }
    return () => { mounted = false; };
  }, [showCreateDialog, dialogBmOptions.length, dialogFormenOptions.length]);

  useEffect(() => {
    if (!showCreateDialog) return;
    let mounted = true;
    const loadDirectBmOptions = async () => {
      const roleCandidates = [
        `${API || ''}/api/personnel?role=bolge_muhendisi`,
        `/api/personnel?role=bolge_muhendisi`,
        `http://127.0.0.1:8000/api/personnel?role=bolge_muhendisi`,
        `http://localhost:8000/api/personnel?role=bolge_muhendisi`
      ];
      const fullCandidates = [
        `${API || ''}/api/personnel`,
        `/api/personnel`,
        `http://127.0.0.1:8000/api/personnel`,
        `http://localhost:8000/api/personnel`
      ];

      const tryFetchList = async (urls) => {
        for (const url of urls) {
          try {
            const res = await axios.get(url);
            const list = Array.isArray(res?.data) ? res.data : [];
            if (list.length > 0) return list;
          } catch (e) {
            // Sonraki aday URL denenir
          }
        }
        return [];
      };

      try {
        let list = await tryFetchList(roleCandidates);
        if (list.length === 0) {
          const full = await tryFetchList(fullCandidates);
          list = full.filter(p => isBolgeMuhendisiLike(p));
        }

        if (!mounted) return;
        setDirectBmOptions(list.filter(p => (p?.status || 'active') === 'active'));
      } catch (error) {
        console.error('Direct BM options fetch error:', error);
      }
    };
    loadDirectBmOptions();
    return () => { mounted = false; };
  }, [showCreateDialog, isBolgeMuhendisiLike]);

  // Mevcut kullanıcının bilgilerini al
  const getCurrentUser = () => {
    const authData = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
    // Çoklu panelde kullanıcı karışmasını önlemek için panel prop'unu birincil kimlik kabul et.
    const resolvedId = personnelId || effectivePersonnelId || authData.personnelId || null;
    const currentPerson = visibilityPersonnel?.find(p => p?.id === resolvedId);

    return {
      id: resolvedId,
      name: currentPerson?.name || authData.personnelName || authData.name || 'Bilinmeyen',
      role: userRole || authData.userRole || null
    };
  };

  useEffect(() => {
    myResponsesRef.current = myResponses || {};
  }, [myResponses]);

  const fetchSurveys = useCallback(async (options = {}) => {
    const { silent = false, isRetry = false, preloaded = null } = options;
    try {
      if (!silent) setLoading(true);
      let allSurveys;
      if (preloaded && Array.isArray(preloaded.surveys)) {
        allSurveys = preloaded.surveys;
        if (preloaded.my_responses && typeof preloaded.my_responses === 'object') {
          myResponsesRef.current = preloaded.my_responses;
          setMyResponses(preloaded.my_responses);
        }
      } else {
        const response = await axios.get(`${API}/api/overtime-surveys`);
        allSurveys = response.data;
      }

      // Mevcut kullanıcı bilgilerini al
      const currentUser = getCurrentUser();
      if (!currentUser?.id) {
        // Kullanıcı kimliği henüz oluşmadıysa yanlış negatif üretmemek için tüm listeyi göster.
        setSurveys(allSurveys);
        return;
      }
      const currentPerson = visibilityPersonnel?.find(p => p.id === currentUser.id);
      
      // Hiyerarşi henüz yüklenmediyse (çoklu panel / geç gelen personnel) panel kullanıcısına
      // hedefli veya hedefsiz anketleri gizleme; yanlış negatif önlenir.
      const hierarchyUnknown = !currentPerson && !!currentUser.id;
      
      // HİYERARŞİK ANKET GÖRÜNÜRLÜğü
      // Yeni mantık: target_formen_id ve target_bolge_muhendisi_id alanlarına göre filtreleme
      
      const filteredSurveys = allSurveys.filter(survey => {
        const currentUserName = String(currentUser?.name || '').trim().toLowerCase();
        const surveyCreatorName = String(survey?.created_by_name || '').trim().toLowerCase();
        const hasMyResponse = !!myResponsesRef.current[survey.id];
        const hasMyDecline = Array.isArray(survey?.declined_by)
          && survey.declined_by.some(d => d?.personnel_id === currentUser?.id);
        const isBasicRole = ['park_gorevlisi'].includes(normalizedUserRole);

        // Müdür tüm anketleri her durumda görebilmeli (eski/bozuk creator kayıtları dahil).
        if (normalizedUserRole === 'mudur') return true;

        // Kişi ankete zaten yanıt verdiyse/gelemiyorum işaretlediyse görünür kalsın.
        if (hasMyResponse || hasMyDecline) return true;

        // Kendi oluşturduğu anketi her zaman görebilir
        if (survey.created_by === currentUser.id) return true;
        // Veri tutarsızlıklarında (aynı kişi farklı id) isim fallback'i
        if (currentUserName && surveyCreatorName && currentUserName === surveyCreatorName) return true;
        
        // Hiyerarşi bilinmiyorsa (currentPerson yok, panel kullanıcısı belli): bu kullanıcıya hedefli veya hedefsiz anketi göster
        if (hierarchyUnknown) {
          if (survey.target_formen_id === currentUser.id || survey.target_bolge_muhendisi_id === currentUser.id) return true;
          if (!survey.target_formen_id && !survey.target_bolge_muhendisi_id) return true;
        }
        
        // Hedefli anketler için creator kaydı zorunlu olmamalı.
        // (Eski/silinmiş personel kayıtlarında da hedefleme alanlarıyla görünürlük çalışsın)
        const creator = visibilityPersonnel?.find(p => p.id === survey.created_by);
        const creatorRole = survey.created_by_role || creator?.role;
        
        // === YENİ: HİYERARŞİK HEDEFLEME KONTROLÜ ===
        // Eğer anket belirli bir formen veya BM'ye hedeflenmiş ise
        
        // Hedef Formen belirlenmişse
        if (survey.target_formen_id) {
          // Personel/hiyerarşi listesi henüz oturmadıysa temel rolde anketi gizleme.
          if (isBasicRole && !currentPerson) return true;
          // Ben hedef formen miyim?
          if (currentUser.id === survey.target_formen_id) return true;
          // Ben hedef formenin ekibinden miyim?
          if (isUnderFormen(currentPerson, survey.target_formen_id)) return true;
          // Hedef BM de belirlenmişse, ben o BM miyim?
          if (survey.target_bolge_muhendisi_id && currentUser.id === survey.target_bolge_muhendisi_id) return true;
          // Ben oluşturanın amiri miyim? (Müdür/Müdür Yrd.)
          if (['mudur', 'mudur_yardimcisi'].includes(userRole)) {
            // Müdür hepsini görür
            if (isMudur) return true;
            // Müdür yardımcısı kendi bölgesindeki anketi görür
            const targetFormen = visibilityPersonnel?.find(p => p.id === survey.target_formen_id);
            if (targetFormen?.mudur_yardimcisi_id === currentUser.id) return true;
          }
          // Hedef BM benim amirim mi? (Formenler için)
          if (survey.target_bolge_muhendisi_id && currentPerson?.bolge_muhendisi_id === survey.target_bolge_muhendisi_id) {
            // Sadece hedef formene bağlıysam görebilirim
            if (isUnderFormen(currentPerson, survey.target_formen_id)) return true;
          }
          // Diğer durumlarda hedefli anketi gösterme
          return false;
        }
        
        // Hedef BM belirlenmişse ama formen belirlenmemişse
        if (survey.target_bolge_muhendisi_id && !survey.target_formen_id) {
          // Personel/hiyerarşi listesi henüz oturmadıysa temel rolde anketi gizleme.
          if (isBasicRole && !currentPerson) return true;
          // Ben hedef BM miyim?
          if (currentUser.id === survey.target_bolge_muhendisi_id) return true;
          // Ben hedef BM'nin altındaki biriyim?
          if (currentPerson?.bolge_muhendisi_id === survey.target_bolge_muhendisi_id) return true;
          // Müdür/Müdür Yrd.
          if (['mudur', 'mudur_yardimcisi'].includes(userRole)) {
            if (isMudur) return true;
            const targetBM = visibilityPersonnel?.find(p => p.id === survey.target_bolge_muhendisi_id);
            if (targetBM?.mudur_yardimcisi_id === currentUser.id) return true;
          }
          return false;
        }
        
        // Hedef belirlenmemişse creator kaydı zorunlu; aksi halde güvenli şekilde gizle.
        // Ancak temel roller (PG/ÇE) için hedefsiz anketleri görünür bırak:
        // hiyerarşi/personnel verisi geç gelse bile anket kaybolmasın.
        if (!creator) {
          if (!survey.target_formen_id && !survey.target_bolge_muhendisi_id && isBasicRole) {
            return true;
          }
          return false;
        }

        // Temel roller için hedefsiz anketleri her zaman göster.
        if (!survey.target_formen_id && !survey.target_bolge_muhendisi_id && isBasicRole) {
          return true;
        }

        // === ESKİ MANTIK: Hedef belirlenmemişse oluşturana göre ===
        
        // === OLUŞTURANIN ALTINDAKİLER ===
        // Formen anketi: formen_id === creator.id olanlar görebilir
        if (creatorRole === 'formen') {
          if (isUnderFormen(currentPerson, survey.created_by)) return true;
        }
        
        // Bölge Mühendisi anketi: bolge_muhendisi_id === creator.id olanlar görebilir
        if (creatorRole === 'bolge_muhendisi') {
          if (currentPerson?.bolge_muhendisi_id === survey.created_by) return true;
        }
        
        // Müdür Yardımcısı anketi: mudur_yardimcisi_id === creator.id olanlar görebilir
        if (creatorRole === 'mudur_yardimcisi') {
          if (currentPerson?.mudur_yardimcisi_id === survey.created_by) return true;
        }
        
        // Müdür anketi: Herkes görebilir
        if (creatorRole === 'mudur') return true;
        
        // === OLUŞTURANIN AMİRLERİ ===
        // Ben oluşturanın amiri miyim? (Yukarı doğru hiyerarşi)
        
        // Formen'in amirleri
        if (creatorRole === 'formen') {
          // Ben bu formenin bölge mühendisi miyim?
          if (creator.bolge_muhendisi_id === currentUser.id) return true;
          // Ben bu formenin müdür yardımcısı mıyım?
          if (creator.mudur_yardimcisi_id === currentUser.id) return true;
          // Ben müdür müyüm?
          if (isMudur) return true;
        }
        
        // Bölge Mühendisi'nin amirleri
        if (creatorRole === 'bolge_muhendisi') {
          // Ben bu bölge mühendisinin müdür yardımcısı mıyım?
          if (creator.mudur_yardimcisi_id === currentUser.id) return true;
          // Ben müdür müyüm?
          if (isMudur) return true;
        }
        
        // Müdür Yardımcısı'nın amirleri
        if (creatorRole === 'mudur_yardimcisi') {
          // Ben müdür müyüm?
          if (isMudur) return true;
        }
        
        return false;
      });
      
      setSurveys(filteredSurveys);
      // 5+ sekme: liste boşsa tek seferlik 3s sonra yeniden dene (kuyrukta kalmış olabilir)
      if (filteredSurveys.length === 0 && !document.hidden && !isRetry) {
        setTimeout(() => {
          if (!document.hidden) fetchSurveys({ silent: true, isRetry: true });
        }, 3000);
      }
    } catch (error) {
      console.error('Surveys fetch error:', error);
      if (!isRetry && !document.hidden) {
        setTimeout(() => fetchSurveys({ silent: true, isRetry: true }), 3000);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [userRole, normalizedUserRole, effectivePersonnelId, visibilityPersonnel, isUnderFormen]);

  const fetchMyAllResponses = useCallback(async () => {
    try {
      const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
      const pid = personnelId || effectivePersonnelId || auth.personnelId;
      if (!pid) return {};
      
      const response = await axios.get(`${API}/api/overtime-survey-responses?personnel_id=${pid}`);
      const responsesMap = {};
      response.data.forEach(r => {
        responsesMap[r.survey_id] = r;
      });
      // Filtreleme fetch'leri ref'ten okuyor; state güncellemesini beklemeden ref'i de güncelle.
      myResponsesRef.current = responsesMap;
      setMyResponses(responsesMap);
      return responsesMap;
    } catch (error) {
      console.error('My responses fetch error:', error);
      return {};
    }
  }, [personnelId, effectivePersonnelId]);

  /** 5+ sekme: tek istek (anket + cevaplar) - tarayıcı bağlantı limiti için */
  const fetchSurveysForUserSingle = useCallback(async (options = {}) => {
    const { silent = false, isRetry = false } = options;
    const pid = personnelId || effectivePersonnelId || (typeof sessionStorage !== 'undefined' && JSON.parse(localStorage.getItem('parkBakimAuth') || '{}').personnelId);
    if (!pid) return fetchSurveys(options);
    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`${API}/api/overtime-surveys/for-user?personnel_id=${encodeURIComponent(pid)}`);
      const data = response.data || {};
      const surveys = Array.isArray(data.surveys) ? data.surveys : [];
      const my_responses = data.my_responses && typeof data.my_responses === 'object' ? data.my_responses : {};
      await fetchSurveys({ ...options, silent: true, preloaded: { surveys, my_responses } });
    } catch (error) {
      console.error('Surveys for-user fetch error:', error);
      if (!isRetry && !document.hidden) setTimeout(() => fetchSurveysForUserSingle({ silent: true, isRetry: true }), 3000);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [personnelId, effectivePersonnelId, fetchSurveys]);

  // Anket sonuçlarını getir
  const fetchSurveyResults = async (surveyId) => {
    try {
      setLoading(true);
      const surveyResponse = await axios.get(`${API}/api/overtime-surveys/${surveyId}`);
      const surveyData = surveyResponse.data || {};
      let responsesData = Array.isArray(surveyData.responses) ? surveyData.responses : [];

      // Modalı anket verisi gelir gelmez aç (plan isteğini bekletme).
      setSelectedSurvey(surveyData);
      setSurveyResponses(responsesData);
      setExistingPlans([]);
      setShowResultsDialog(true);

      // Bazı verilerde survey detail içinde responses boş dönebiliyor; fallback endpoint'i de dene.
      if (!responsesData.length) {
        try {
          const responsesRes = await axios.get(`${API}/api/overtime-survey-responses?survey_id=${surveyId}`);
          responsesData = Array.isArray(responsesRes.data) ? responsesRes.data : [];
          setSurveyResponses(responsesData);
        } catch (respErr) {
          console.warn('Survey responses fallback warning:', respErr);
        }
      }

      // Planları arka planda çek; hata olursa modal açık kalır.
      axios
        .get(`${API}/api/overtime-draft-plans?survey_id=${surveyId}`)
        .then((plansResponse) => {
          setExistingPlans(plansResponse.data || []);
        })
        .catch((plansErr) => {
          console.warn('Draft plans fetch warning:', plansErr);
        });
      
      // Anketi görüntülendi olarak işaretle
      const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
      if (auth.personnelId) {
        try {
          await axios.put(`${API}/api/overtime-surveys/${surveyId}/mark-viewed?personnel_id=${auth.personnelId}`);
          // Badge'ler App.js'deki polling mekanizması tarafından otomatik güncellenir
        } catch (e) {
          console.log('Mark viewed error:', e);
        }
      }
    } catch (error) {
      console.error('Survey results fetch error:', error);
      toast.error('Anket sonuçları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const openSurveyResults = (survey) => {
    if (!survey) return;
    // Önce modalı aç; ağ gecikmesi kullanıcı etkileşimini engellemesin.
    setSelectedSurvey(survey);
    setSurveyResponses(Array.isArray(survey.responses) ? survey.responses : []);
    setExistingPlans([]);
    setShowResultsDialog(true);
    fetchSurveyResults(survey.id);
  };
  
  // Anket sonuçlarını sessizce yenile (loading göstermeden)
  const refreshSurveyResultsSilently = async (surveyId) => {
    try {
      const response = await axios.get(`${API}/api/overtime-surveys/${surveyId}`);
      setSelectedSurvey(response.data);
      setSurveyResponses(response.data.responses || []);
    } catch (error) {
      console.error('Silent refresh error:', error);
    }
  };
  
  // Anket sonuçları açıkken otomatik yenileme (10 saniyede bir)
  useEffect(() => {
    if (!showResultsDialog || !selectedSurvey) return;
    
    const interval = setInterval(() => {
      refreshSurveyResultsSilently(selectedSurvey.id);
    }, 10000); // 10 saniye
    
    return () => clearInterval(interval);
  }, [showResultsDialog, selectedSurvey]);

  // SSE veya sekme değişiminden gelen anket yenileme: tek istek (5+ sekme)
  useEffect(() => {
    const handleRefreshSurveys = () => {
      if (document.hidden) return;
      fetchSurveysForUserSingle({ silent: true });
    };
    window.addEventListener('refreshSurveys', handleRefreshSurveys);
    return () => window.removeEventListener('refreshSurveys', handleRefreshSurveys);
  }, [fetchSurveysForUserSingle]);

  // Personelin mevcut cevabını getir
  const fetchMyResponse = async (surveyId) => {
    try {
      const auth = JSON.parse(localStorage.getItem('parkBakimAuth') || '{}');
      const pid = personnelId || effectivePersonnelId || auth.personnelId;
      if (!pid) {
        setMyCurrentResponse(null);
        setSelectedDates([]);
        return;
      }
      const response = await axios.get(`${API}/api/overtime-survey-responses?survey_id=${surveyId}&personnel_id=${pid}`);
      if (response.data && response.data.length > 0) {
        setMyCurrentResponse(response.data[0]);
        setSelectedDates(response.data[0].available_dates || []);
      } else {
        setMyCurrentResponse(null);
        setSelectedDates([]);
      }
    } catch (error) {
      console.error('My response fetch error:', error);
      setMyCurrentResponse(null);
      setSelectedDates([]);
    }
  };

  // Manuel tarih ekleme
  const handleAddCustomDate = () => {
    if (customDate && !targetDates.includes(customDate)) {
      setTargetDates(prev => [...prev, customDate].sort());
      setCustomDate('');
    }
  };

  // Tarihi listeden kaldır
  const handleRemoveDate = (date) => {
    setTargetDates(prev => prev.filter(d => d !== date));
  };
  
  // 5+ sekme: aynı anda çok istek bağlantıyı dolduruyor; tek gecikmeli istek + boşsa tek retry
  const visibleFetchTimeoutRef = useRef(null);

  const refetchWhenVisible = useCallback(() => {
    if (document.hidden) return;
    if (visibleFetchTimeoutRef.current) clearTimeout(visibleFetchTimeoutRef.current);
    // 1.2s bekle; tek istek kullan (5+ sekme bağlantı limiti)
    visibleFetchTimeoutRef.current = setTimeout(() => {
      visibleFetchTimeoutRef.current = null;
      if (document.hidden) return;
      fetchSurveysForUserSingle({ silent: true, isRetry: false });
    }, 1200);
  }, [fetchSurveysForUserSingle]);

  useEffect(() => {
    const onVisible = () => {
      if (document.hidden) return;
      refetchWhenVisible();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      if (visibleFetchTimeoutRef.current) clearTimeout(visibleFetchTimeoutRef.current);
    };
  }, [refetchWhenVisible]);

  // Periyodik yenileme (sadece görünürken, 8 sn)
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return;
      fetchSurveys({ silent: true });
    }, 8000);
    return () => clearInterval(interval);
  }, [fetchSurveys]);

  // İlk yükleme: sekme görünürse 1.2s sonra tek istek (5+ panel bağlantı limiti)
  useEffect(() => {
    let mounted = true;
    if (!document.hidden) {
      const t = setTimeout(() => {
        if (!mounted || document.hidden) return;
        fetchSurveysForUserSingle({ silent: true, isRetry: false });
      }, 1200);
      return () => { mounted = false; clearTimeout(t); };
    }
    return () => { mounted = false; };
  }, [fetchSurveysForUserSingle]);

  // Plan oluşturulduktan sonra anket sonuçlarına dönmek için event listener
  useEffect(() => {
    const handleOpenSurveyResults = async (event) => {
      const { surveyId } = event.detail;
      if (surveyId) {
        // Anketi bul ve sonuçlar modalını aç
        try {
          const response = await axios.get(`${API}/api/overtime-surveys/${surveyId}`);
          setSelectedSurvey(response.data);
          
          // Sonuçları çek
          const responsesRes = await axios.get(`${API}/api/overtime-survey-responses?survey_id=${surveyId}`);
          setSurveyResponses(responsesRes.data);
          
          // Planları çek
          const plansRes = await axios.get(`${API}/api/overtime-draft-plans?survey_id=${surveyId}`);
          setExistingPlans(plansRes.data);
          
          // Modalı aç
          setShowResultsDialog(true);
        } catch (e) {
          console.error('Anket sonuçları açılamadı:', e);
        }
      }
    };

    window.addEventListener('openSurveyResults', handleOpenSurveyResults);
    return () => window.removeEventListener('openSurveyResults', handleOpenSurveyResults);
  }, []);

  const handleCreateSurvey = async () => {
    if (!surveyTitle.trim() || targetDates.length === 0 || !deadlineDate) {
      toast.error('Lütfen anket başlığı, hedef tarihler ve bitiş tarihi girin');
      return;
    }
    
    // Bölge Mühendisi veya üst roller için formen seçimi zorunlu
    if (['bolge_muhendisi', 'mudur_yardimcisi', 'mudur'].includes(normalizedUserRole) && !selectedSurveyFormen) {
      toast.error('Lütfen hedef formen seçin');
      return;
    }
    
    // Müdür/Müdür Yrd. için BM seçimi de zorunlu
    if (['mudur_yardimcisi', 'mudur'].includes(normalizedUserRole) && !selectedSurveyBolgeMuh) {
      toast.error('Lütfen hedef bölge mühendisi seçin');
      return;
    }
    
    // Aynı tarihler için aynı BM'ye zaten anket var mı kontrol et
    const targetBMId = selectedSurveyBolgeMuh || (normalizedUserRole === 'bolge_muhendisi' ? personnelId : null);
    if (targetBMId) {
      const existingSurvey = surveys.find(s => {
        if (s.status !== 'active') return false;
        const sameBM = s.target_bolge_muhendisi_id === targetBMId || s.created_by === targetBMId;
        if (!sameBM) return false;
        // Tarih çakışması kontrolü
        const hasDateConflict = s.target_dates?.some(d => targetDates.includes(d));
        return hasDateConflict;
      });
      
      if (existingSurvey) {
        const conflictDates = existingSurvey.target_dates?.filter(d => targetDates.includes(d));
        const bmName = visibilityPersonnel?.find(p => p.id === targetBMId)?.name || 'Bu BM';
        toast.error(`${bmName} için ${conflictDates?.join(', ')} tarih(ler)inde zaten aktif bir anket var: "${existingSurvey.title}"`);
        return;
      }
    }

    try {
      setLoading(true);
      const currentUser = getCurrentUser();
      if (!currentUser?.id) {
        toast.error('Kullanıcı bilgisi okunamadı. Sayfayı yenileyip tekrar deneyin.');
        return;
      }
      
      // Formen'in bölgesini hedef bölge olarak ayarla
      const currentPerson = visibilityPersonnel?.find(p => p.id === currentUser.id);
      const targetRegionId = currentPerson?.region_ids?.[0] || null; // İlk bölge
      
      // Hedef formen ve BM bilgileri
      const targetFormenData = selectedSurveyFormen ? visibilityPersonnel?.find(p => p.id === selectedSurveyFormen) : null;
      const targetBMData = selectedSurveyBolgeMuh ? visibilityPersonnel?.find(p => p.id === selectedSurveyBolgeMuh) : null;
      
      await axios.post(
        `${API}/api/overtime-surveys`,
        {
          title: surveyTitle,
          description: surveyDescription,
          target_dates: targetDates,
          target_region_id: targetRegionId,
          deadline: `${deadlineDate}T${deadlineTime}:00`,
          // Hiyerarşik hedef bilgileri
          target_formen_id: selectedSurveyFormen || null,
          target_formen_name: targetFormenData?.name || null,
          target_bolge_muhendisi_id: selectedSurveyBolgeMuh || null,
          target_bolge_muhendisi_name: targetBMData?.name || null,
          // Oluşturan katılım durumu (Formen ve BM için)
          creator_joining: creatorJoining,
          creator_not_joining_reason: creatorJoining === false ? creatorNotJoiningReason : null
        },
        {
          params: {
            created_by: currentUser.id,
            created_by_name: currentUser.name,
            created_by_role: currentUser.role || normalizedUserRole || null
          }
        }
      );

      toast.success('Anket oluşturuldu');
      setShowCreateDialog(false);
      // Form temizle
      setSurveyTitle('');
      setSurveyDescription('');
      setTargetDates([]);
      setDeadlineDate('');
      setSelectedSurveyFormen(null);
      setSelectedSurveyBolgeMuh(null);
      setCreatorJoining(null);
      setCreatorNotJoiningReason('');
      fetchSurveys();
    } catch (error) {
      toast.error('Anket oluşturulamadı: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitResponse = async (confirmed = false) => {
    // Eğer mevcut cevap varsa ve onay alınmadıysa, onay iste
    if (myCurrentResponse && !confirmed) {
      setShowConfirmDialog(true);
      return;
    }
    
    try {
      setIsSubmittingResponse(true);
      const currentUser = getCurrentUser();
      
      // Anket süresinin dolup dolmadığını kontrol et
      const deadline = new Date(selectedSurvey.deadline);
      const now = new Date();
      const isExpired = now > deadline;
      
      if (isExpired && !canBypassDeadline) {
        toast.error('Anket süresi dolmuş. Değişiklik için amirinizi arayın.');
        return;
      }

      // Eğer hiç tarih seçilmemişse ve mevcut cevap varsa, cevabı sil (iptal)
      if (selectedDates.length === 0 && myCurrentResponse) {
        await axios.delete(`${API}/api/overtime-survey-responses/${myCurrentResponse.id}?deleted_by=${currentUser.id}&deleted_by_name=${encodeURIComponent(currentUser.name)}&bypass_deadline=${canBypassDeadline && isExpired}`);
        toast.success('Cevabınız iptal edildi');
      } else if (selectedDates.length === 0) {
        toast.error('Lütfen en az bir tarih seçin veya mevcut cevabı iptal edin');
        return;
      } else {
        // Cevap gönder veya güncelle
        const requestData = {
          survey_id: selectedSurvey.id,
          personnel_id: currentUser.id,
          personnel_name: currentUser.name,
          available_dates: selectedDates,
          bypass_deadline: canBypassDeadline && isExpired
        };
        
        // Eğer amir başkasının cevabını değiştiriyorsa
        if (canBypassDeadline && isExpired) {
          requestData.modified_by = currentUser.id;
          requestData.modified_by_name = currentUser.name;
        }
        
        await axios.post(`${API}/api/overtime-survey-responses`, requestData);
        toast.success(myCurrentResponse ? 'Cevabınız güncellendi' : 'Cevabınız kaydedildi');
      }

      setShowResponseDialog(false);
      setShowConfirmDialog(false);
      setSelectedDates([]);
      setMyCurrentResponse(null);
      fetchSurveys();
      fetchMyAllResponses();
      
      // Ankete cevap verildiğinde görüldü olarak işaretle
      try {
        await axios.put(`${API}/api/overtime-surveys/${selectedSurvey.id}/mark-viewed?personnel_id=${currentUser.id}`);
        // Badge'ler App.js'deki polling mekanizması tarafından otomatik güncellenir
      } catch (e) {
        console.log('Mark viewed after response error:', e);
      }
    } catch (error) {
      toast.error('Cevap gönderilemedi: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSubmittingResponse(false);
    }
  };

  const handleCloseSurvey = async (surveyId) => {
    try {
      await axios.put(`${API}/api/overtime-surveys/${surveyId}/close`);
      toast.success('Anket kapatıldı');
      fetchSurveys();
    } catch (error) {
      toast.error('Anket kapatılamadı');
    }
  };

  // Silme onay diyalogunu aç
  const openDeleteConfirm = (survey) => {
    setSurveyToDelete(survey);
    setShowDeleteConfirm(true);
  };

  // Silme işlemini gerçekleştir
  const handleDeleteSurvey = async () => {
    if (!surveyToDelete) return;
    try {
      await axios.delete(`${API}/api/overtime-surveys/${surveyToDelete.id}`);
      toast.success('Anket silindi');
      setShowDeleteConfirm(false);
      setSurveyToDelete(null);
      fetchSurveys();
    } catch (error) {
      toast.error('Anket silinemedi');
    }
  };

  const toggleTargetDate = (date) => {
    setTargetDates(prev => 
      prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
    );
  };

  const toggleSelectedDate = (date) => {
    setSelectedDates(prev => 
      prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
    );
  };

  // Önümüzdeki 14 günü getir
  const getUpcomingDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dayOfWeek = date.getDay();
      // Sadece Cumartesi (6) ve Pazar (0) günlerini göster
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        dates.push({
          date: format(date, 'yyyy-MM-dd'),
          label: format(date, 'd MMMM yyyy, EEEE', { locale: tr }),
          isWeekend: true
        });
      }
    }
    return dates;
  };

  const upcomingDates = getUpcomingDates();

  // Tarihleri sırala (ay içinde küçükten büyüğe, ay geçişlerinde doğru sıralama)
  const sortDates = (dates) => {
    if (!dates || dates.length === 0) return [];
    return [...dates].sort((a, b) => new Date(a) - new Date(b));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">📊 Mesai Anketi</h3>
        </div>
        {canCreateSurvey && (
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Yeni Anket Oluştur
          </Button>
        )}
      </div>

      {/* Aktif Anketler */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {surveys.filter(s => s.status === 'active').map(survey => (
          <Card key={survey.id} className="border-green-300 bg-green-50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <span className="animate-pulse">🟢</span>
                  {survey.title}
                </CardTitle>
                <Badge className="bg-green-500">Aktif</Badge>
              </div>
              {survey.created_by_name && (
                <p className="text-xs text-gray-500 mt-1">
                  📝 Oluşturan: <span className="font-medium">{survey.created_by_name}</span>
                  {survey.target_formen_name && (
                    <span className="ml-2">
                      → <span className="text-blue-600">{survey.target_formen_name}</span> (Formen)
                      {survey.target_bolge_muhendisi_name && (
                        <span>, <span className="text-cyan-600">{survey.target_bolge_muhendisi_name}</span> (BM)</span>
                      )}
                    </span>
                  )}
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {survey.description && (
                <p className="text-sm text-gray-600">{survey.description}</p>
              )}
              
              <div className="text-sm">
                <strong>Tarihler:</strong>
                <div className="flex flex-wrap gap-1 mt-1">
                  {sortDates(survey.target_dates)?.map(date => {
                    const isSelected = myResponses[survey.id]?.available_dates?.includes(date);
                    return (
                      <Badge 
                        key={date} 
                        variant={isSelected ? "default" : "outline"}
                        className={isSelected ? "bg-green-500 text-white" : ""}
                      >
                        {format(parseISO(date), 'd MMM, EEEE', { locale: tr })}
                        {isSelected && <CheckCircle className="h-3 w-3 ml-1" />}
                      </Badge>
                    );
                  })}
                </div>
                {myResponses[survey.id] && (
                  <p className="text-xs text-green-600 mt-1">
                    ✅ Cevapladınız ({sortDates(myResponses[survey.id].available_dates)?.map(d => format(parseISO(d), 'd MMM', { locale: tr })).join(', ')})
                  </p>
                )}
                {/* Gelemiyorum olarak işaretlediyse göster */}
                {survey.declined_by?.some(d => d.personnel_id === effectivePersonnelId) && (
                  <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                    <X className="h-3 w-3" />
                    Gelemiyorum olarak işaretlediniz
                  </p>
                )}
              </div>
              
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-orange-500" />
                <span>Bitiş: {format(parseISO(survey.deadline), 'd MMMM yyyy HH:mm', { locale: tr })}</span>
              </div>
              
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <Users className="h-4 w-4 text-blue-500" />
                <span>{survey.response_count || 0} kişi cevapladı</span>
                {(survey.declined_by?.length || 0) > 0 && (
                  <span className="text-orange-600">· {survey.declined_by.length} gelemiyorum</span>
                )}
              </div>
              
              <div className="flex flex-wrap gap-2 mt-3">
                {/* Cevapla veya Değiştir butonu - Müdür ve Müdür Yardımcısı cevap veremez */}
                {canRespond && (
                  <Button 
                    size="sm" 
                    variant={myResponses[survey.id] ? "outline" : "default"}
                    className={myResponses[survey.id] ? "border-green-500 text-green-700" : ""}
                    onClick={() => {
                      // Önce modalı aç, sonra veriyi yükle (hızlı açılsın)
                      setSelectedSurvey(survey);
                      setShowResponseDialog(true);
                      // Arka planda mevcut cevabı yükle
                      fetchMyResponse(survey.id);
                    }}
                  >
                    {myResponses[survey.id] ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Değiştir
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Cevapla
                      </>
                    )}
                  </Button>
                )}
                
                {/* Gelemiyorum butonu - Müdür/Müd.Yrd. değilse göster */}
                {canRespond && (
                  survey.declined_by?.some(d => d.personnel_id === effectivePersonnelId) ? (
                    // Zaten gelemiyorum olarak işaretlemiş
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="bg-orange-100 text-orange-700 border-orange-400 cursor-default"
                      disabled
                      title={survey.declined_by?.find(d => d.personnel_id === effectivePersonnelId)?.reason || 'Sebep belirtilmedi'}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Gelemiyorum
                    </Button>
                  ) : myResponses[survey.id] ? (
                    // Cevap vermiş - Gelemiyorum silik/disabled
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-gray-400 border-gray-300 cursor-not-allowed"
                      disabled
                      title="Cevabınızı silmek için Değiştir'e tıklayın"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Gelemiyorum
                    </Button>
                  ) : (
                    // Henüz cevap vermemiş - aktif
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                      onClick={() => {
                        setSurveyToDecline(survey);
                        setDeclineReason('');
                        setShowDeclineDialog(true);
                      }}
                      title="Mesaiye gelemiyorum"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Gelemiyorum
                    </Button>
                  )
                )}
                
                {/* Sonuçlar/Önizleme butonu - cevapladıysa veya amir ise göster */}
                {(myResponses[survey.id] || canCreateSurvey) && (
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => openSurveyResults(survey)}
                    title="Anket sonuçlarını gör"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Sonuçlar
                  </Button>
                )}
                
                {/* Anketi Kapat butonu - sadece anketi oluşturan görebilir */}
                {survey.created_by === effectivePersonnelId && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-gray-600 border-gray-300"
                    onClick={() => {
                      setSurveyToClose(survey);
                      setShowCloseConfirm(true);
                    }}
                  >
                    Anketi Kapat
                  </Button>
                )}
                
                {/* Müdür/Müdür Yardımcısı Silme Butonu */}
                {canDeleteSurvey && (
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => openDeleteConfirm(survey)}
                    title="Anketi sil"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        
        {/* Yüklenirken boş mesaj gösterme, yükleme bittikten sonra göster */}
        {!loading && surveys.filter(s => s.status === 'active').length === 0 && (
          <Card className="col-span-2 border-dashed">
            <CardContent className="py-8 text-center text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Aktif anket bulunmuyor</p>
              {canCreateSurvey && (
                <Button 
                  variant="outline" 
                  className="mt-3"
                  onClick={() => setShowCreateDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  İlk Anketi Oluştur
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Kapatılmış Anketler */}
      {surveys.filter(s => s.status === 'closed').length > 0 && (
        <div className="mt-6">
          <h4 className="font-semibold text-gray-700 mb-3">Kapatılmış Anketler</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {surveys.filter(s => s.status === 'closed').slice(0, 6).map(survey => (
              <Card key={survey.id} className="bg-gray-50">
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{survey.title}</p>
                      <p className="text-xs text-gray-500">{survey.response_count || 0} cevap{(survey.declined_by?.length || 0) > 0 ? ` · ${survey.declined_by.length} gelemiyorum` : ""}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => openSurveyResults(survey)}
                        title="Anket sonuçlarını gör"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canDeleteSurvey && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => openDeleteConfirm(survey)}
                          title="Anketi sil"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Anket Oluşturma Dialog */}
      <DraggableModal
        isOpen={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
          setSelectedSurveyFormen(null);
          setSelectedSurveyBolgeMuh(null);
          setCreatorJoining(null);
          setCreatorNotJoiningReason('');
        }}
        title="Yeni Mesai Anketi Oluştur"
        maxWidth="max-w-2xl"
      >
        <div className="space-y-4">
          {/* Hiyerarşik Hedef Seçimi - Müdür/Müdür Yardımcısı için */}
          {['mudur_yardimcisi', 'mudur'].includes(normalizedUserRole) && (
            <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200 space-y-3">
              <Label className="text-cyan-700 font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Hedef Seçimi *
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs text-gray-600">Bölge Mühendisi</Label>
                  <SearchableSelect
                    value={selectedSurveyBolgeMuh || ''}
                    onChange={(v) => {
                      const val = v || null;
                      setSelectedSurveyBolgeMuh(val);
                      setSelectedSurveyFormen(null);
                    }}
                    allLabel="Bölge mühendisi seçin..."
                    allValue=""
                    searchPlaceholder="Bölge mühendisi ara..."
                    className="w-full"
                    options={visibleBolgeMuhendisleri.map(bm => ({ id: bm.id, name: bm.name }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-gray-600">Formen</Label>
                  <SearchableSelect
                    value={selectedSurveyFormen || ''}
                    onChange={(v) => setSelectedSurveyFormen(v || null)}
                    disabled={!selectedSurveyBolgeMuh}
                    allLabel={selectedSurveyBolgeMuh ? 'Formen seçin...' : 'Önce BM seçin'}
                    allValue=""
                    searchPlaceholder="Formen ara..."
                    className="w-full"
                    options={formenlerForSelectedBM.map(f => ({ id: f.id, name: f.name }))}
                  />
                </div>
              </div>
              {visibleBolgeMuhendisleri.length === 0 && (
                <p className="text-xs text-red-700 bg-red-100 p-2 rounded">
                  Bu kullanıcı için eşleşen bölge mühendisi bulunamadı. Yönetim hiyerarşisi alanlarını kontrol edin.
                </p>
              )}
              {selectedSurveyFormen && selectedSurveyBolgeMuh && (
                <p className="text-xs text-cyan-700 bg-cyan-100 p-2 rounded">
                  📧 Anket: <strong>{visibilityPersonnel.find(p => p.id === selectedSurveyBolgeMuh)?.name}</strong> (BM), 
                  <strong> {visibilityPersonnel.find(p => p.id === selectedSurveyFormen)?.name}</strong> (Formen) 
                  ve ekibine gönderilecek
                </p>
              )}
            </div>
          )}
          
          {/* Hiyerarşik Hedef Seçimi - Bölge Mühendisi için */}
          {normalizedUserRole === 'bolge_muhendisi' && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
              <Label className="text-blue-700 font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Hedef Formen Seçimi *
              </Label>
              <SearchableSelect
                value={selectedSurveyFormen || ''}
                onChange={(v) => setSelectedSurveyFormen(v || null)}
                allLabel="Formen seçin..."
                allValue=""
                searchPlaceholder="Formen ara..."
                className="w-full"
                options={formenlerForCurrentBM.map(f => ({ id: f.id, name: f.name }))}
              />
              {selectedSurveyFormen && (
                <p className="text-xs text-blue-700 bg-blue-100 p-2 rounded">
                  📧 Anket: Siz, <strong>{visibilityPersonnel.find(p => p.id === selectedSurveyFormen)?.name}</strong> (Formen)
                  ve ekibine gönderilecek
                </p>
              )}
            </div>
          )}
          
          {/* Formen için bilgi mesajı */}
          {isFormen && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700 flex items-center gap-2">
                <Users className="h-4 w-4" />
                📧 Anket: Siz, ekibiniz ve amiriniz (Bölge Mühendisi) görecek
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label>Anket Başlığı *</Label>
            <Input 
              value={surveyTitle}
              onChange={(e) => setSurveyTitle(e.target.value)}
              placeholder="Örn: 11-12 Ocak Hafta Sonu Mesai Anketi"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Açıklama (Opsiyonel)</Label>
            <Textarea 
              value={surveyDescription}
              onChange={(e) => setSurveyDescription(e.target.value)}
              placeholder="Anket hakkında ek bilgi..."
              rows={2}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Mesai Tarihleri * (Hangi günler için?)</Label>
            
            {/* Manuel Tarih Ekleme */}
            <div className="flex gap-2 mb-2 items-start">
              <div className="flex-1">
                <DatePicker
                  value={customDate}
                  onChange={setCustomDate}
                  min={todayYmd()}
                  placeholder="Tarih ekle"
                  highlightHolidays
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleAddCustomDate}
                disabled={!customDate}
                className="mt-1"
              >
                <Plus className="h-4 w-4 mr-1" />
                Ekle
              </Button>
            </div>
            
            {/* Seçili Tarihler */}
            {targetDates.length > 0 && (
              <div className="flex flex-wrap gap-2 p-2 bg-blue-50 rounded border border-blue-200 mb-2">
                {targetDates.map(date => (
                  <Badge 
                    key={date} 
                    className="bg-blue-100 text-blue-800 cursor-pointer hover:bg-red-100 hover:text-red-800 flex items-center gap-1"
                    onClick={() => handleRemoveDate(date)}
                  >
                    {format(parseISO(date), 'd MMM, EEEE', { locale: tr })}
                    <X className="h-3 w-3" />
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Hızlı Seçim - Önümüzdeki Hafta Sonları */}
            <p className="text-xs text-gray-500 font-medium">Hızlı Seçim (Hafta Sonları):</p>
            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border rounded">
              {upcomingDates.map(item => (
                <div 
                  key={item.date}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm ${
                    targetDates.includes(item.date) 
                      ? 'bg-blue-100 border border-blue-300' 
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                  onClick={() => toggleTargetDate(item.date)}
                >
                  <Checkbox checked={targetDates.includes(item.date)} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500">Seçili: {targetDates.length} gün</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <DatePicker
                label="Anket Bitiş Tarihi"
                required
                value={deadlineDate}
                onChange={setDeadlineDate}
              />
            </div>
            <div className="space-y-2">
              <Label>Bitiş Saati *</Label>
              <Input 
                type="time"
                value={deadlineTime}
                onChange={(e) => setDeadlineTime(e.target.value)}
              />
            </div>
          </div>
          
          {/* Oluşturan Katılım Durumu - Formen ve BM için */}
          {['formen', 'bolge_muhendisi'].includes(userRole) && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
              <Label className="text-amber-700 font-medium">Siz de bu mesaiye katılacak mısınız?</Label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="creatorJoining" 
                    checked={creatorJoining === true} 
                    onChange={() => {
                      setCreatorJoining(true);
                      setCreatorNotJoiningReason('');
                    }}
                    className="w-4 h-4 text-green-600"
                  />
                  <span className="text-sm text-green-700">✓ Evet, katılacağım</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="creatorJoining" 
                    checked={creatorJoining === false} 
                    onChange={() => setCreatorJoining(false)}
                    className="w-4 h-4 text-red-600"
                  />
                  <span className="text-sm text-red-700">✗ Hayır, katılamayacağım</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="creatorJoining" 
                    checked={creatorJoining === null} 
                    onChange={() => {
                      setCreatorJoining(null);
                      setCreatorNotJoiningReason('');
                    }}
                    className="w-4 h-4 text-gray-600"
                  />
                  <span className="text-sm text-gray-600">Henüz bilmiyorum</span>
                </label>
              </div>
              {creatorJoining === false && (
                <div className="mt-2">
                  <Label className="text-xs text-amber-700 mb-1 block">Katılmama Sebebi (Opsiyonel)</Label>
                  <Input
                    placeholder="Örn: İzinli, Raporlu, Toplantı..."
                    value={creatorNotJoiningReason}
                    onChange={(e) => setCreatorNotJoiningReason(e.target.value)}
                    className="text-sm"
                  />
                </div>
              )}
            </div>
          )}
          
          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="flex-1">
              İptal
            </Button>
            <Button onClick={handleCreateSurvey} disabled={loading} className="flex-1">
              {loading ? 'Oluşturuluyor...' : 'Anket Oluştur'}
            </Button>
          </div>
        </div>
      </DraggableModal>

      {/* Anket Cevaplama Dialog */}
      <DraggableModal
        isOpen={showResponseDialog}
        onClose={() => {
          setShowResponseDialog(false);
          setMyCurrentResponse(null);
          setSelectedDates([]);
        }}
        title={`Anket: ${selectedSurvey?.title || ''}`}
        maxWidth="max-w-md"
      >
        {selectedSurvey && (
          <div className="space-y-4">
            {/* Gelemiyorum işaretliyse uyarı */}
            {selectedSurvey.declined_by?.some(d => d.personnel_id === personnelId) && (
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-300">
                <p className="text-sm font-medium text-orange-800">
                  ❌ Gelemiyorum olarak işaretlediniz
                </p>
                <p className="text-xs text-orange-600 mt-1">
                  Gün seçebilmek için önce "Gelemiyorum" işaretini kaldırmanız gerekiyor.
                </p>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="mt-2 text-orange-700 border-orange-400"
                  onClick={async () => {
                    try {
                      // Gelemiyorum'u kaldır
                      await axios.put(`${API}/api/overtime-surveys/${selectedSurvey.id}/remove-decline?personnel_id=${personnelId}`);
                      toast.success('Gelemiyorum işareti kaldırıldı');
                      // Anketi yenile
                      const response = await axios.get(`${API}/api/overtime-surveys/${selectedSurvey.id}`);
                      setSelectedSurvey(response.data);
                      fetchSurveys();
                    } catch (e) {
                      toast.error('İşlem başarısız: ' + (e.response?.data?.detail || e.message));
                    }
                  }}
                >
                  Gelemiyorum İşaretini Kaldır
                </Button>
              </div>
            )}
            
            {/* Anket süresi kontrolü */}
            {new Date() > new Date(selectedSurvey.deadline) && (
              <div className={`p-3 rounded-lg ${canBypassDeadline ? 'bg-yellow-50 border border-yellow-300' : 'bg-red-50 border border-red-300'}`}>
                <p className={`text-sm font-medium ${canBypassDeadline ? 'text-yellow-800' : 'text-red-800'}`}>
                  ⚠️ Anket süresi dolmuş!
                </p>
                <p className={`text-xs ${canBypassDeadline ? 'text-yellow-600' : 'text-red-600'}`}>
                  {canBypassDeadline 
                    ? 'Amir olarak süre dolmuş olsa da değişiklik yapabilirsiniz.'
                    : 'Değişiklik için amirinizi arayın.'}
                </p>
              </div>
            )}
            
            {/* Mevcut cevap bilgisi */}
            {myCurrentResponse && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-800">
                  📋 Daha önce cevapladınız
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Seçtiğiniz günler: {myCurrentResponse.available_dates?.map(d => format(parseISO(d), 'd MMM', { locale: tr })).join(', ')}
                </p>
                {myCurrentResponse.modified_by_name && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠️ Son değişiklik: {myCurrentResponse.modified_by_name} tarafından
                  </p>
                )}
              </div>
            )}
            
            {/* Gelemiyorum işaretliyse gün seçimi devre dışı */}
            {selectedSurvey.declined_by?.some(d => d.personnel_id === personnelId) ? (
              <p className="text-sm text-gray-500 italic">
                Gün seçimi için önce yukarıdaki "Gelemiyorum İşaretini Kaldır" butonuna tıklayın.
              </p>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  {myCurrentResponse 
                    ? 'Cevabınızı değiştirebilir veya iptal edebilirsiniz:'
                    : 'Aşağıdaki tarihlerden hangilerinde mesaiye gelebilirsiniz?'}
                </p>
                
                <div className="space-y-2">
                  {selectedSurvey.target_dates?.map(date => (
                    <div 
                      key={date}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        selectedDates.includes(date) 
                          ? 'bg-green-100 border-2 border-green-400' 
                          : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                      } ${new Date() > new Date(selectedSurvey.deadline) && !canBypassDeadline ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => {
                        if (new Date() > new Date(selectedSurvey.deadline) && !canBypassDeadline) {
                          toast.error('Anket süresi dolmuş. Değişiklik için amirinizi arayın.');
                          return;
                        }
                        toggleSelectedDate(date);
                      }}
                    >
                      <Checkbox checked={selectedDates.includes(date)} />
                      <div>
                        <p className="font-medium">{format(parseISO(date), 'EEEE', { locale: tr })}</p>
                        <p className="text-sm text-gray-500">{format(parseISO(date), 'd MMMM yyyy', { locale: tr })}</p>
                      </div>
                      {selectedDates.includes(date) && (
                        <CheckCircle className="h-5 w-5 text-green-500 ml-auto" />
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
            
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowResponseDialog(false)} className="flex-1">
                Kapat
              </Button>
              {myCurrentResponse && (
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSelectedDates([]);
                  }}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  disabled={new Date() > new Date(selectedSurvey.deadline) && !canBypassDeadline}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  İptal Et
                </Button>
              )}
              <Button 
                onClick={handleSubmitResponse} 
                disabled={isSubmittingResponse || (new Date() > new Date(selectedSurvey.deadline) && !canBypassDeadline)} 
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {isSubmittingResponse ? 'Gönderiliyor...' : (
                  selectedDates.length === 0 && myCurrentResponse
                    ? '🗑️ Cevabı Sil'
                    : myCurrentResponse 
                      ? `✏️ Güncelle (${selectedDates.length} gün)`
                      : `✅ Gelebilirim (${selectedDates.length} gün)`
                )}
              </Button>
            </div>
          </div>
        )}
      </DraggableModal>

      {/* Değişiklik Onay Dialogu */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Değişiklik Onayı</AlertDialogTitle>
            <AlertDialogDescription>
              Cevabınızı değiştirmek istediğinize emin misiniz?
              {selectedDates.length === 0 ? (
                <span className="block mt-2 text-red-600 font-medium">
                  ⚠️ Hiç gün seçmediniz. Bu işlem mevcut cevabınızı silecektir.
                </span>
              ) : (
                <span className="block mt-2">
                  Yeni seçiminiz: {selectedDates.map(d => format(parseISO(d), 'd MMM', { locale: tr })).join(', ')}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowConfirmDialog(false)}>
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleSubmitResponse(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              Evet, Değiştir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Anket Sonuçları Modalı */}
      <DraggableModal
        isOpen={showResultsDialog}
        onClose={() => {
          setShowResultsDialog(false);
          setSelectedSurvey(null);
          setSurveyResponses([]);
        }}
        title={`📊 Anket Sonuçları: ${selectedSurvey?.title || ''}`}
        maxWidth="max-w-3xl"
      >
        {selectedSurvey && (() => {
          // ÖNİZLEME MODU: Müdür/Müdür Yrd. oluşturduysa ve görüntüleyen BM/Formen ise
          const hierarchyPersonnel = (visibilityPersonnel && visibilityPersonnel.length > 0) ? visibilityPersonnel : personnel;
          const currentViewer = getCurrentUser();
          const viewerId = currentViewer?.id || effectivePersonnelId;
          const viewerRole = normalizedUserRole;
          const viewerPerson = hierarchyPersonnel.find(p => p.id === viewerId);
          const isViewerSurveyOwner = selectedSurvey.created_by === viewerId;
          const surveyCreatorRole = selectedSurvey.created_by_role || hierarchyPersonnel.find(p => p.id === selectedSurvey.created_by)?.role;
          const isCreatorHighLevel = ['mudur', 'mudur_yardimcisi'].includes(surveyCreatorRole);
          const isViewerLowerLevel = ['bolge_muhendisi', 'formen'].includes(viewerRole);
          const isPreviewMode = isCreatorHighLevel && isViewerLowerLevel;
          
          return (
          <div className="space-y-4 p-4">
            {/* Önizleme Modu Uyarısı */}
            {isPreviewMode && (
              <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 p-3 rounded-lg text-sm">
                <strong>📋 Önizleme Modu:</strong> Bu anketi {selectedSurvey.created_by_name} ({getRoleTitle(surveyCreatorRole)}) oluşturduğu için sadece sonuçları görüntüleyebilirsiniz.
              </div>
            )}
            
            {/* Özet Bilgiler */}
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm text-gray-600">Toplam Cevap</p>
                  <p className="text-2xl font-bold text-blue-600">{surveyResponses.length}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Bitiş Tarihi</p>
                  <p className="font-medium">{format(parseISO(selectedSurvey.deadline), 'd MMM yyyy HH:mm', { locale: tr })}</p>
                </div>
                <Badge className={selectedSurvey.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}>
                  {selectedSurvey.status === 'active' ? 'Aktif' : 'Kapalı'}
                </Badge>
              </div>
              {/* Anketi Oluşturan Bilgisi */}
              <div className="border-t border-blue-200 pt-2 mt-2">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Anketi Oluşturan:</span>{' '}
                  <span className="text-blue-700">{selectedSurvey.created_by_name || 'Bilinmiyor'}</span>
                  {selectedSurvey.created_at && (
                    <span className="text-gray-500 ml-2">
                      ({format(parseISO(selectedSurvey.created_at), 'd MMM yyyy HH:mm', { locale: tr })})
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Gün Gün Katılım Listesi */}
            <div className="space-y-3">
              <h4 className="font-semibold">📅 Tarihlere Göre Katılım</h4>
              {sortDates(selectedSurvey.target_dates)?.map(date => {
                // Cevabın rolü boş olabilir (eski/migrasyon öncesi kayıtlar) →
                // bu durumda personel listesinden çöz. Aksi halde rolü null olan
                // bir formen/BM `!== 'formen'` kontrolünü geçip park atanabilir
                // listesine sızıyordu (alan denetimi yapmaları gerekirken).
                const roleOfResponse = (r) =>
                  r.personnel_role || hierarchyPersonnel.find(p => p?.id === r.personnel_id)?.role;
                // Formen ve BM'leri listeden hariç tut - onlar alan denetimi yapar, park ataması yapılmaz
                const respondentsForDate = surveyResponses.filter(r => {
                  const role = roleOfResponse(r);
                  return r.available_dates?.includes(date) &&
                    role !== 'formen' &&
                    role !== 'bolge_muhendisi';
                });
                return (
                  <div key={date} className="bg-gray-50 p-3 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-800">
                        {format(parseISO(date), 'd MMMM yyyy, EEEE', { locale: tr })}
                      </span>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-500">
                          {respondentsForDate.length} kişi
                        </Badge>
                        {/* Plan Oluştur/Değiştir Butonu - Sadece anket oluşturabilenler görebilir */}
                        {/* Müdür/Müdür Yrd. oluşturduysa BM/Formen sadece önizleme yapabilir */}
                        {(() => {
                          const surveyCreatorRole = selectedSurvey.created_by_role;
                          const isCreatorHighLevel = ['mudur', 'mudur_yardimcisi'].includes(surveyCreatorRole);
                          const isViewerLowerLevel = ['bolge_muhendisi', 'formen'].includes(userRole);
                          
                          // Müdür/Müdür Yrd. oluşturduysa ve görüntüleyen BM/Formen ise buton gösterme
                          if (isCreatorHighLevel && isViewerLowerLevel) {
                            return null; // Sadece önizleme - buton yok
                          }
                          
                          // Normal durumda buton göster
                          if (canCreateSurvey && onCreatePlanFromSurvey) {
                            const existingPlan = existingPlans.find(p => p.target_date === date);
                            const hasPlan = !!existingPlan;
                            
                            return (
                              <div className="flex items-center gap-2">
                                {hasPlan && (
                                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                                    ✓ Plan Oluşturuldu
                                  </span>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className={`h-7 text-xs ${hasPlan 
                                    ? 'border-orange-400 text-orange-600 hover:bg-orange-50' 
                                    : 'border-blue-400 text-blue-600 hover:bg-blue-50'}`}
                                  onClick={() => {
                                    // Formenler ve BM'ler plan oluşturmada personel olarak atanmaz - alan denetimi yaparlar
                                    // (rol boşsa personel listesinden çöz — sızıntı önlemi)
                                    const personnelForPlan = respondentsForDate
                                      .filter(r => {
                                        const role = roleOfResponse(r);
                                        return role !== 'formen' && role !== 'bolge_muhendisi';
                                      })
                                      .map(r => ({
                                        id: r.personnel_id,
                                        name: r.personnel_name,
                                        role: roleOfResponse(r) || 'park_gorevlisi'
                                      }));
                                    
                                    const planData = {
                                      surveyId: selectedSurvey.id,
                                      surveyTitle: selectedSurvey.title,
                                      targetDate: date,
                                      existingPlanId: existingPlan?.id || null,
                                      availablePersonnel: personnelForPlan
                                    };
                                    onCreatePlanFromSurvey(planData);
                                    // Modal açık kalsın - diğer günler için de plan oluşturabilsin
                                  }}
                                >
                                  <CalendarClock className="h-3 w-3 mr-1" />
                                  {hasPlan ? 'Değiştir' : 'Plan Oluştur'}
                                </Button>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                    {respondentsForDate.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {respondentsForDate.map((r, idx) => (
                          <div 
                            key={idx} 
                            className="flex items-center gap-1 bg-white px-2 py-1 rounded border text-sm"
                          >
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            <span>{r.personnel_name}</span>
                            {r.modified_by_name && (
                              <span className="text-orange-500 text-xs">(değiştirildi)</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">Bu gün için henüz kimse cevap vermedi</p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Formen Onay Bölümü - Sadece amir rolleri görür, önizleme modunda düzenleme yapılamaz */}
            {/* Formen Bölümü - BM, MY, Müdür görebilir ve düzenleyebilir */}
            {/* Cevap veren Formen'i bul (target_formen_id yoksa cevaplardan bul) */}
            {(viewerRole === 'bolge_muhendisi' || ['mudur', 'mudur_yardimcisi'].includes(viewerRole)) && (() => {
              // Önce target_formen_id'den, yoksa cevaplardan Formen'i bul
              let targetFormenId = selectedSurvey.target_formen_id;
              if (!targetFormenId) {
                // Cevap veren Formen'i bul
                const formenResponse = surveyResponses.find(r => r.personnel_role === 'formen');
                targetFormenId = formenResponse?.personnel_id;
              }
              if (!targetFormenId) return null;
              
              const targetFormen = hierarchyPersonnel.find(p => p.id === targetFormenId);
              if (!targetFormen) return null;
              
              // Formenin cevabını bul
              const formenResponse = surveyResponses.find(r => r.personnel_id === targetFormenId);
              const formenDeclined = selectedSurvey.declined_by?.some(d => d.personnel_id === targetFormenId);
              
              // BM, MY ve Müdür düzenleyebilir (preview mode değilse)
              const canEdit = ['bolge_muhendisi', 'mudur', 'mudur_yardimcisi'].includes(viewerRole) && !isPreviewMode;
              
              return (
                <div className={`p-3 rounded-lg border ${isPreviewMode ? 'bg-gray-50 border-gray-200' : 'bg-purple-50 border-purple-200'}`}>
                  <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isPreviewMode ? 'text-gray-600' : 'text-purple-800'}`}>
                    <Users className="h-4 w-4" />
                    Formen: {targetFormen.name}
                    {isPreviewMode && <span className="text-xs text-gray-400">(salt okunur)</span>}
                    {canEdit && <span className="text-xs text-purple-500">(düzenlenebilir)</span>}
                  </h4>
                  
                  {formenDeclined ? (
                    <p className="text-sm text-orange-600">❌ Gelemiyorum olarak işaretlemiş</p>
                  ) : formenResponse ? (
                    <div className="space-y-2">
                      <p className={`text-sm ${isPreviewMode ? 'text-gray-600' : 'text-purple-700'}`}>Gelebileceği günler:</p>
                      <div className="flex flex-wrap gap-2">
                        {formenResponse.available_dates?.map(date => (
                          <div key={date} className={`flex items-center gap-2 bg-white px-3 py-1.5 rounded border ${isPreviewMode ? 'border-gray-300' : 'border-purple-300'}`}>
                            <Checkbox 
                              checked={formenResponse.approved_dates?.includes(date)}
                              disabled={isPreviewMode || !canEdit}
                              onCheckedChange={async (checked) => {
                                if (isPreviewMode || !canEdit) return;
                                try {
                                  // Formenin bu tarihini onayla/onayı kaldır
                                  const currentApproved = formenResponse.approved_dates || [];
                                  const newApproved = checked 
                                    ? [...currentApproved, date]
                                    : currentApproved.filter(d => d !== date);
                                  
                                  await axios.put(`${API}/api/overtime-survey-responses/${formenResponse.id}/approve`, {
                                    approved_dates: newApproved,
                                    approved_by: viewerId,
                                    approved_by_name: hierarchyPersonnel.find(p => p.id === viewerId)?.name
                                  });
                                  
                                  // Yenile
                                  refreshSurveyResultsSilently(selectedSurvey.id);
                                  toast.success(checked ? 'Onaylandı' : 'Onay kaldırıldı');
                                } catch (e) {
                                  toast.error('İşlem başarısız');
                                }
                              }}
                            />
                            <span className="text-sm font-medium">{format(parseISO(date), 'd MMM, EEEE', { locale: tr })}</span>
                            {formenResponse.approved_dates?.includes(date) && (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Henüz cevap vermedi</p>
                  )}
                </div>
              );
            })()}

            {/* Bölge Mühendisi Bölümü - MY ve Müdür görebilir ve düzenleyebilir */}
            {/* Cevap veren BM'yi bul (target_bolge_muhendisi_id yoksa cevaplardan bul) */}
            {['mudur', 'mudur_yardimcisi'].includes(viewerRole) && (() => {
              // Önce target_bolge_muhendisi_id'den, yoksa cevaplardan BM'yi bul
              let targetBMId = selectedSurvey.target_bolge_muhendisi_id;
              if (!targetBMId) {
                // Cevap veren BM'yi bul
                const bmResponse = surveyResponses.find(r => r.personnel_role === 'bolge_muhendisi');
                targetBMId = bmResponse?.personnel_id;
              }
              // Ayrıca anketi oluşturan BM ise onu da göster
              if (!targetBMId && selectedSurvey.created_by_role === 'bolge_muhendisi') {
                targetBMId = selectedSurvey.created_by;
              }
              if (!targetBMId) return null;
              
              const targetBM = hierarchyPersonnel.find(p => p.id === targetBMId);
              if (!targetBM) return null;
              
              // BM'nin cevabını bul
              const bmResponse = surveyResponses.find(r => r.personnel_id === targetBMId);
              const bmDeclined = selectedSurvey.declined_by?.some(d => d.personnel_id === targetBMId);
              
              // MY ve Müdür düzenleyebilir
              const canEdit = ['mudur', 'mudur_yardimcisi'].includes(viewerRole) && !isPreviewMode;
              
              return (
                <div className={`p-3 rounded-lg border ${isPreviewMode ? 'bg-gray-50 border-gray-200' : 'bg-cyan-50 border-cyan-200'}`}>
                  <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isPreviewMode ? 'text-gray-600' : 'text-cyan-800'}`}>
                    <Users className="h-4 w-4" />
                    Bölge Mühendisi: {targetBM.name}
                    {isPreviewMode && <span className="text-xs text-gray-400">(salt okunur)</span>}
                    {canEdit && <span className="text-xs text-cyan-500">(düzenlenebilir)</span>}
                  </h4>
                  
                  {bmDeclined ? (
                    <p className="text-sm text-orange-600">❌ Gelemiyorum olarak işaretlemiş</p>
                  ) : bmResponse ? (
                    <div className="space-y-2">
                      <p className={`text-sm ${isPreviewMode ? 'text-gray-600' : 'text-cyan-700'}`}>Gelebileceği günler:</p>
                      <div className="flex flex-wrap gap-2">
                        {bmResponse.available_dates?.map(date => (
                          <div key={date} className={`flex items-center gap-2 bg-white px-3 py-1.5 rounded border ${isPreviewMode ? 'border-gray-300' : 'border-cyan-300'}`}>
                            <Checkbox 
                              checked={bmResponse.approved_dates?.includes(date)}
                              disabled={isPreviewMode || !canEdit}
                              onCheckedChange={async (checked) => {
                                if (isPreviewMode || !canEdit) return;
                                try {
                                  // BM'nin bu tarihini onayla/onayı kaldır
                                  const currentApproved = bmResponse.approved_dates || [];
                                  const newApproved = checked 
                                    ? [...currentApproved, date]
                                    : currentApproved.filter(d => d !== date);
                                  
                                  await axios.put(`${API}/api/overtime-survey-responses/${bmResponse.id}/approve`, {
                                    approved_dates: newApproved,
                                    approved_by: viewerId,
                                    approved_by_name: hierarchyPersonnel.find(p => p.id === viewerId)?.name
                                  });
                                  
                                  // Yenile
                                  refreshSurveyResultsSilently(selectedSurvey.id);
                                  toast.success(checked ? 'Onaylandı' : 'Onay kaldırıldı');
                                } catch (e) {
                                  toast.error('İşlem başarısız');
                                }
                              }}
                            />
                            <span className="text-sm font-medium">{format(parseISO(date), 'd MMM, EEEE', { locale: tr })}</span>
                            {bmResponse.approved_dates?.includes(date) && (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Henüz cevap vermedi</p>
                  )}
                </div>
              );
            })()}

            {/* Formen için BM Bölümü - Sadece görüntüleme (düzenleme yok) */}
            {/* Cevap veren BM'yi bul (target_bolge_muhendisi_id yoksa cevaplardan bul) */}
            {viewerRole === 'formen' && (() => {
              // Önce target_bolge_muhendisi_id'den, yoksa cevaplardan BM'yi bul
              let targetBMId = selectedSurvey.target_bolge_muhendisi_id;
              if (!targetBMId) {
                // Cevap veren BM'yi bul
                const bmResponse = surveyResponses.find(r => r.personnel_role === 'bolge_muhendisi');
                targetBMId = bmResponse?.personnel_id;
              }
              // Ayrıca anketi oluşturan BM ise onu da göster
              if (!targetBMId && selectedSurvey.created_by_role === 'bolge_muhendisi') {
                targetBMId = selectedSurvey.created_by;
              }
              if (!targetBMId) return null;
              
              const targetBM = hierarchyPersonnel.find(p => p.id === targetBMId);
              if (!targetBM) return null;
              
              // BM'nin cevabını bul
              const bmResponse = surveyResponses.find(r => r.personnel_id === targetBMId);
              const bmDeclined = selectedSurvey.declined_by?.some(d => d.personnel_id === targetBMId);
              
              return (
                <div className="p-3 rounded-lg border bg-cyan-50 border-cyan-200">
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-cyan-800">
                    <Users className="h-4 w-4" />
                    Bölge Mühendisi: {targetBM.name}
                    <span className="text-xs text-cyan-500">(sadece görüntüleme)</span>
                  </h4>
                  
                  {bmDeclined ? (
                    <p className="text-sm text-orange-600">❌ Gelemiyorum olarak işaretlemiş</p>
                  ) : bmResponse ? (
                    <div className="space-y-2">
                      <p className="text-sm text-cyan-700">Gelebileceği günler:</p>
                      <div className="flex flex-wrap gap-2">
                        {bmResponse.available_dates?.map(date => (
                          <div key={date} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded border border-cyan-300">
                            <CheckCircle className="h-4 w-4 text-cyan-600" />
                            <span className="text-sm font-medium">{format(parseISO(date), 'd MMM, EEEE', { locale: tr })}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Henüz cevap vermedi</p>
                  )}
                </div>
              );
            })()}

            {/* Tüm Cevaplar Özeti - görüntüleyenin hiyerarşisine göre filtrele */}
            {/* Formen: Kendi ekibi + Kendi BM'sinin cevabı (sadece görüntüleme) */}
            <div>
              <h4 className="font-semibold mb-2">✅ Gelebilecekler ({(() => {
                let filtered = surveyResponses;
                if (viewerRole === 'formen') {
                  if (isViewerSurveyOwner) return filtered.length;
                  filtered = filtered.filter(r => {
                    const person = hierarchyPersonnel.find(p => p.id === r.personnel_id);
                    // Kendi ekibi
                    if (person?.formen_id === viewerId) return true;
                    // Kendi BM'sinin cevabı (sadece görüntüleme için dahil et)
                    if (viewerPerson?.bolge_muhendisi_id && r.personnel_id === viewerPerson.bolge_muhendisi_id) return true;
                    return false;
                  });
                } else if (viewerRole === 'bolge_muhendisi') {
                  filtered = filtered.filter(r => {
                    const person = hierarchyPersonnel.find(p => p.id === r.personnel_id);
                    if (!person) return false;
                    if (person.bolge_muhendisi_id === viewerId) return true;
                    const formen = hierarchyPersonnel.find(f => f.id === person.formen_id);
                    return formen?.bolge_muhendisi_id === viewerId;
                  });
                } else if (viewerRole === 'mudur_yardimcisi') {
                  filtered = filtered.filter(r => {
                    const person = hierarchyPersonnel.find(p => p.id === r.personnel_id);
                    if (!person) return false;
                    if (person.mudur_yardimcisi_id === viewerId) return true;
                    const formen = hierarchyPersonnel.find(f => f.id === person.formen_id);
                    if (formen?.mudur_yardimcisi_id === viewerId) return true;
                    const bm = hierarchyPersonnel.find(b => b.id === person.bolge_muhendisi_id);
                    return bm?.mudur_yardimcisi_id === viewerId;
                  });
                }
                return filtered.length;
              })()})</h4>
              {(() => {
                let filteredResponses = surveyResponses;
                if (viewerRole === 'formen') {
                  if (isViewerSurveyOwner) {
                    return (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {filteredResponses.map((response, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 rounded-lg border text-sm bg-white hover:bg-gray-50">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span className="font-medium">{response.personnel_name}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {sortDates(response.available_dates)?.map(date => (
                                <Badge key={date} variant="outline" className="text-xs">
                                  {format(parseISO(date), 'd MMM', { locale: tr })}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  filteredResponses = filteredResponses.filter(r => {
                    const person = hierarchyPersonnel.find(p => p.id === r.personnel_id);
                    // Kendi ekibi
                    if (person?.formen_id === viewerId) return true;
                    // Kendi BM'sinin cevabı (sadece görüntüleme için dahil et)
                    if (viewerPerson?.bolge_muhendisi_id && r.personnel_id === viewerPerson.bolge_muhendisi_id) return true;
                    return false;
                  });
                } else if (viewerRole === 'bolge_muhendisi') {
                  filteredResponses = filteredResponses.filter(r => {
                    const person = hierarchyPersonnel.find(p => p.id === r.personnel_id);
                    if (!person) return false;
                    if (person.bolge_muhendisi_id === viewerId) return true;
                    const formen = hierarchyPersonnel.find(f => f.id === person.formen_id);
                    return formen?.bolge_muhendisi_id === viewerId;
                  });
                } else if (viewerRole === 'mudur_yardimcisi') {
                  filteredResponses = filteredResponses.filter(r => {
                    const person = hierarchyPersonnel.find(p => p.id === r.personnel_id);
                    if (!person) return false;
                    if (person.mudur_yardimcisi_id === viewerId) return true;
                    const formen = hierarchyPersonnel.find(f => f.id === person.formen_id);
                    if (formen?.mudur_yardimcisi_id === viewerId) return true;
                    const bm = hierarchyPersonnel.find(b => b.id === person.bolge_muhendisi_id);
                    return bm?.mudur_yardimcisi_id === viewerId;
                  });
                }
                
                if (filteredResponses.length === 0) {
                  return (
                    <div className="text-center py-4 text-gray-500">
                      <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      <p>Henüz kimse cevaplamadı</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {filteredResponses.map((response, idx) => {
                      // Formen için BM cevabı mı kontrol et
                      const isBmResponse = viewerRole === 'formen' && viewerPerson?.bolge_muhendisi_id === response.personnel_id;
                      
                      return (
                        <div key={idx} className={`flex items-center justify-between p-2 rounded-lg border text-sm ${
                          isBmResponse 
                            ? 'bg-cyan-50 border-cyan-200 hover:bg-cyan-100' 
                            : 'bg-white hover:bg-gray-50'
                        }`}>
                          <div className="flex items-center gap-2">
                            <CheckCircle className={`h-4 w-4 ${isBmResponse ? 'text-cyan-600' : 'text-green-600'}`} />
                            <span className="font-medium">{response.personnel_name}</span>
                            {isBmResponse && (
                              <Badge variant="outline" className="text-[10px] bg-cyan-100 text-cyan-700 border-cyan-300">
                                🔍 BM (Sadece Görüntüleme)
                              </Badge>
                            )}
                            {response.modified_by_name && (
                              <span className="text-xs text-orange-600">(değiştiren: {response.modified_by_name})</span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {sortDates(response.available_dates)?.map(date => (
                              <Badge key={date} variant="outline" className="text-xs">
                                {format(parseISO(date), 'd MMM', { locale: tr })}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Gelemiyorum Diyenler - görüntüleyenin hiyerarşisine göre filtrele */}
            {(() => {
              // Görüntüleyen kişinin hiyerarşisine göre filtrele
              let filteredDeclined = selectedSurvey.declined_by || [];
              
              if (viewerRole === 'formen') {
                if (!isViewerSurveyOwner) {
                // Formen kendi ekibini + kendi BM'sini görür
                  filteredDeclined = filteredDeclined.filter(d => {
                    const person = hierarchyPersonnel.find(p => p.id === d.personnel_id);
                    // Kendi ekibi
                    if (person?.formen_id === viewerId) return true;
                    // Kendi BM'si
                    if (viewerPerson?.bolge_muhendisi_id && d.personnel_id === viewerPerson.bolge_muhendisi_id) return true;
                    return false;
                  });
                }
              } else if (viewerRole === 'bolge_muhendisi') {
                // Bölge mühendisi kendi bölgesindeki personeli görür
                filteredDeclined = filteredDeclined.filter(d => {
                  const person = hierarchyPersonnel.find(p => p.id === d.personnel_id);
                  if (!person) return false;
                  // Direkt bağlı personel
                  if (person.bolge_muhendisi_id === viewerId) return true;
                  // Formen aracılığıyla bağlı personel
                  const formen = hierarchyPersonnel.find(f => f.id === person.formen_id);
                  return formen?.bolge_muhendisi_id === viewerId;
                });
              } else if (viewerRole === 'mudur_yardimcisi') {
                // Müdür yardımcısı kendi sorumluluğundaki personeli görür
                filteredDeclined = filteredDeclined.filter(d => {
                  const person = hierarchyPersonnel.find(p => p.id === d.personnel_id);
                  if (!person) return false;
                  if (person.mudur_yardimcisi_id === viewerId) return true;
                  const formen = hierarchyPersonnel.find(f => f.id === person.formen_id);
                  if (formen?.mudur_yardimcisi_id === viewerId) return true;
                  const bm = hierarchyPersonnel.find(b => b.id === person.bolge_muhendisi_id);
                  return bm?.mudur_yardimcisi_id === viewerId;
                });
              }
              // Müdür tüm listeyi görür
              
              if (filteredDeclined.length === 0) return null;
              
              return (
                <div>
                  <h4 className="font-semibold mb-2 text-orange-600">❌ Gelemiyorum Diyenler ({filteredDeclined.length})</h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {filteredDeclined.map((declined, idx) => {
                      // Formen için BM cevabı mı kontrol et
                      const isBmDeclined = viewerRole === 'formen' && viewerPerson?.bolge_muhendisi_id === declined.personnel_id;
                      
                      return (
                        <div key={idx} className={`flex items-center justify-between p-2 rounded-lg border text-sm ${
                          isBmDeclined 
                            ? 'bg-cyan-50 border-cyan-200' 
                            : 'bg-orange-50 border-orange-200'
                        }`}>
                          <div className="flex items-center gap-2">
                            <X className={`h-4 w-4 ${isBmDeclined ? 'text-cyan-600' : 'text-orange-600'}`} />
                            <span className="font-medium">{declined.personnel_name}</span>
                            {isBmDeclined && (
                              <Badge variant="outline" className="text-[10px] bg-cyan-100 text-cyan-700 border-cyan-300">
                                🔍 BM (Sadece Görüntüleme)
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {declined.declined_at && format(parseISO(declined.declined_at), 'd MMM HH:mm', { locale: tr })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Henüz Cevap Vermeyenler - ANKETİN HEDEFİNE göre filtrele */}
            {(() => {
              // Anketi görebilen personeli hesapla ve cevap vermeyenleri bul
              const respondedIds = surveyResponses.map(r => r.personnel_id);
              const declinedIds = selectedSurvey.declined_by?.map(d => d.personnel_id) || [];
              const allRespondedOrDeclinedIds = [...respondedIds, ...declinedIds];
              
              // ÖNCELİKLE: Anketin hedefine göre eligiblePersonnel belirle
              let eligiblePersonnel = [];
              
              // Eğer anket belirli bir formene hedeflenmiş ise
              if (selectedSurvey.target_formen_id) {
                // Sadece hedef formenin ekibi (çim ekibi + park görevlisi)
                eligiblePersonnel = hierarchyPersonnel.filter(p => 
                  p.formen_id === selectedSurvey.target_formen_id && 
                  ['park_gorevlisi'].includes(p.role)
                );
                // Hedef formeni de ekle
                const targetFormen = hierarchyPersonnel.find(p => p.id === selectedSurvey.target_formen_id);
                if (targetFormen && !eligiblePersonnel.find(p => p.id === targetFormen.id)) {
                  eligiblePersonnel.push(targetFormen);
                }
                // Hedef BM'yi de ekle (eğer varsa)
                if (selectedSurvey.target_bolge_muhendisi_id) {
                  const targetBM = hierarchyPersonnel.find(p => p.id === selectedSurvey.target_bolge_muhendisi_id);
                  if (targetBM && !eligiblePersonnel.find(p => p.id === targetBM.id)) {
                    eligiblePersonnel.push(targetBM);
                  }
                }
              } 
              // Eğer anket belirli bir BM'ye hedeflenmiş ama formen belirlenmemişse
              else if (selectedSurvey.target_bolge_muhendisi_id && !selectedSurvey.target_formen_id) {
                // BM'nin altındaki tüm personel
                eligiblePersonnel = hierarchyPersonnel.filter(p => {
                  if (['mudur', 'mudur_yardimcisi'].includes(p.role)) return false;
                  if (p.bolge_muhendisi_id === selectedSurvey.target_bolge_muhendisi_id) return true;
                  const formen = hierarchyPersonnel.find(f => f.id === p.formen_id);
                  return formen?.bolge_muhendisi_id === selectedSurvey.target_bolge_muhendisi_id;
                });
                // BM'yi de ekle
                const targetBM = hierarchyPersonnel.find(p => p.id === selectedSurvey.target_bolge_muhendisi_id);
                if (targetBM && !eligiblePersonnel.find(p => p.id === targetBM.id)) {
                  eligiblePersonnel.push(targetBM);
                }
              }
              // Hedef belirlenmemişse: Anketi oluşturanın hiyerarşisine göre
              else {
                const creatorRole = selectedSurvey.created_by_role || hierarchyPersonnel.find(p => p.id === selectedSurvey.created_by)?.role;
                const creatorId = selectedSurvey.created_by;
                
                if (creatorRole === 'formen') {
                  // Formen'in ekibi
                  eligiblePersonnel = hierarchyPersonnel.filter(p => 
                    p.formen_id === creatorId && 
                    ['park_gorevlisi'].includes(p.role)
                  );
                  // Formeni de ekle
                  const creator = hierarchyPersonnel.find(p => p.id === creatorId);
                  if (creator && !eligiblePersonnel.find(p => p.id === creator.id)) {
                    eligiblePersonnel.push(creator);
                  }
                  // BM'yi de ekle (amiri)
                  const bm = hierarchyPersonnel.find(p => p.id === creator?.bolge_muhendisi_id);
                  if (bm && !eligiblePersonnel.find(p => p.id === bm.id)) {
                    eligiblePersonnel.push(bm);
                  }
                } else if (creatorRole === 'bolge_muhendisi') {
                  // BM'nin altındaki tüm personel
                  eligiblePersonnel = hierarchyPersonnel.filter(p => {
                    if (['mudur', 'mudur_yardimcisi'].includes(p.role)) return false;
                    if (p.bolge_muhendisi_id === creatorId) return true;
                    const formen = hierarchyPersonnel.find(f => f.id === p.formen_id);
                    return formen?.bolge_muhendisi_id === creatorId;
                  });
                  // BM'yi de ekle
                  const creator = hierarchyPersonnel.find(p => p.id === creatorId);
                  if (creator && !eligiblePersonnel.find(p => p.id === creator.id)) {
                    eligiblePersonnel.push(creator);
                  }
                } else if (creatorRole === 'mudur_yardimcisi') {
                  // Müdür yardımcısının sorumluluğundaki personel
                  eligiblePersonnel = hierarchyPersonnel.filter(p => {
                    if (['mudur', 'mudur_yardimcisi'].includes(p.role)) return false;
                    if (p.mudur_yardimcisi_id === creatorId) return true;
                    const formen = hierarchyPersonnel.find(f => f.id === p.formen_id);
                    if (formen?.mudur_yardimcisi_id === creatorId) return true;
                    const bm = hierarchyPersonnel.find(b => b.id === p.bolge_muhendisi_id);
                    return bm?.mudur_yardimcisi_id === creatorId;
                  });
                } else if (creatorRole === 'mudur') {
                  // Müdür tüm alt kademeyi görür
                  eligiblePersonnel = hierarchyPersonnel.filter(p => 
                    !['mudur'].includes(p.role)
                  );
                }
              }
              
              const notResponded = eligiblePersonnel.filter(p => 
                !allRespondedOrDeclinedIds.includes(p.id)
              ).sort((a, b) => a.name.localeCompare(b.name, 'tr')); // Alfabetik sırala

              if (viewerRole === 'formen' && isViewerSurveyOwner) {
                // Anket sahibi formen tüm kendi anket kitlesini görür.
                // Ek filtre uygulamadan devam et.
              }
              
              if (notResponded.length === 0) return null;
              
              return (
                <div>
                  <h4 className="font-semibold mb-2 text-gray-500">⏳ Henüz Cevap Vermeyenler ({notResponded.length})</h4>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                    {notResponded.map((person, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded border border-gray-300 text-sm text-gray-600"
                      >
                        <Clock className="h-3 w-3" />
                        <span>{person.name}</span>
                        <span className="text-xs text-gray-400">({roleLabels[person.role] || person.role})</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Kapatma Butonu */}
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setShowResultsDialog(false)}>
                Kapat
              </Button>
            </div>
          </div>
          );
        })()}
      </DraggableModal>

      {/* Anket Silme Onay Diyalogu */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Anketi Sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>&quot;{surveyToDelete?.title}&quot;</strong> anketini silmek istediğinize emin misiniz?
              <br /><br />
              Bu işlem geri alınamaz ve ankete verilen tüm cevaplar da silinecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDeleteConfirm(false);
              setSurveyToDelete(null);
            }}>
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSurvey}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Evet, Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Anketi Kapatma Onay Diyalogu */}
      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Anketi Kapat</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>&quot;{surveyToClose?.title}&quot;</strong> anketini kapatmak istediğinize emin misiniz?
              <br /><br />
              Kapatılan ankete artık cevap verilemez. Sonuçları görüntüleyebilirsiniz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowCloseConfirm(false);
              setSurveyToClose(null);
            }}>
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                if (surveyToClose) {
                  await handleCloseSurvey(surveyToClose.id);
                  setShowCloseConfirm(false);
                  setSurveyToClose(null);
                }
              }}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Evet, Anketi Kapat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Gelemiyorum Dialog - Neden sorması için */}
      <DraggableModal
        isOpen={showDeclineDialog}
        onClose={() => {
          setShowDeclineDialog(false);
          setSurveyToDecline(null);
          setDeclineReason('');
        }}
        title="Mesaiye Gelemiyorum"
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            <strong>&quot;{surveyToDecline?.title}&quot;</strong> anketindeki mesaiye gelemeyeceğinizi bildiriyorsunuz.
          </p>
          
          <div className="space-y-2">
            <Label className="text-sm">Gelememe Sebebi (Opsiyonel)</Label>
            <Input
              placeholder="Örn: İzinli, Raporlu, Aile ziyareti..."
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
            />
            <p className="text-xs text-gray-500">Bu bilgi amiriniz tarafından görülebilir.</p>
          </div>
          
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeclineDialog(false);
                setSurveyToDecline(null);
                setDeclineReason('');
              }} 
              className="flex-1"
            >
              Vazgeç
            </Button>
            <Button 
              onClick={async () => {
                // Güvenilir kimlik: panel prop'u (effectivePersonnelId), auth.personnelId değil
                // (auth bazı panellerde personnelId taşımıyor → "undefined" gönderiliyordu).
                const pid = effectivePersonnelId;
                if (!pid) { toast.error('Kullanıcı kimliği bulunamadı, sayfayı yenileyin.'); return; }
                try {
                  // Gelemiyorum olarak işaretle (neden ile birlikte)
                  const reasonParam = declineReason ? `&reason=${encodeURIComponent(declineReason)}` : '';
                  await axios.put(`${API}/api/overtime-surveys/${surveyToDecline.id}/decline?personnel_id=${encodeURIComponent(pid)}${reasonParam}`);
                  toast.success('Gelemiyorum olarak işaretlendi');
                  setShowDeclineDialog(false);
                  setSurveyToDecline(null);
                  setDeclineReason('');
                  // Yenile
                  fetchSurveys();
                } catch (e) {
                  console.log('Decline error:', e);
                  toast.error('İşlem başarısız: ' + (e.response?.data?.detail || e.message));
                }
              }}
              className="flex-1 bg-orange-600 hover:bg-orange-700"
            >
              <X className="h-4 w-4 mr-1" />
              Gelemiyorum
            </Button>
          </div>
        </div>
      </DraggableModal>
    </div>
  );
};

// NOT: Bu dosyadaki yerel PlanDetailContent kopyası KALDIRILDI. Dosya içinde
// hiç render edilmiyordu ve gövdesi tanımsız rol bayraklarına (isMudur,
// isMudurYardimcisi, isBolgeMuhendisi) başvuruyordu. Canlı sürüm ayrı dosyada:
// components/mesai/PlanDetailContent.jsx (components/mesai/index.js üzerinden).


export default MesaiAnketiSection;
